import { InfoCard, Link as BackstageLink, MarkdownContent } from '@backstage/core-components';
import { storageApiRef, useApi } from '@backstage/core-plugin-api';
import { releaseNotesApiRef, useReleaseNotes } from '@internal/backstage-plugin-release-notes-react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, makeStyles } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { formatDate, formatRelative } from './homeUtils';
import { useHomeCardFooterStyles } from './HomeCardFooter';

const RELEASE_NOTES_PATH = '/release-notes';
const releaseNoteViewPath = (id: string) => `${RELEASE_NOTES_PATH}/view/${encodeURIComponent(id)}`;

const STORAGE_BUCKET = 'devhub-home';
const LAST_SEEN_RELEASE_KEY = 'whatsNewLastSeenReleaseId';
// Only pop the dialog for reasonably fresh releases so first-time visitors
// are not greeted with months-old news.
const DIALOG_MAX_AGE_DAYS = 30;

export type LatestReleaseNote = ReturnType<typeof useReleaseNotes>['releaseNotes']['results'][number] & {
  source?: string;
  url?: string;
};

export const releaseNoteLink = (note?: LatestReleaseNote): string =>
  note?.source === 'github' && note.url && /^https?:\/\//i.test(note.url) ? note.url : note ? releaseNoteViewPath(note.id) : RELEASE_NOTES_PATH;

/** Latest DevHub release note, or undefined while loading or on error (fails soft). */
export const useLatestReleaseNote = (): LatestReleaseNote | undefined => {
  const { releaseNotes, loading, error, retry } = useReleaseNotes({ max: 1 });
  useEffect(() => {
    const timer = setInterval(retry, 60_000);
    return () => clearInterval(timer);
  }, [retry]);
  if (loading || error) return undefined;
  return releaseNotes.results[0];
};

const useStyles = makeStyles(theme => ({
  dialogMeta: {
    marginBottom: theme.spacing(2),
  },
}));

/** Always-visible latest-release CTA, backed by the same live data as the welcome dialog. */
export const LatestReleaseNotesCard = ({ latest }: { latest?: LatestReleaseNote }) => {
  const footer = useHomeCardFooterStyles();
  return (
    <InfoCard
      title="Latest release notes"
      variant="fullHeight"
      actionsClassName={footer.actions}
      actions={
        <BackstageLink to={releaseNoteLink(latest)} className={footer.link}>
          {latest ? 'View our latest release notes' : 'Release notes'}
        </BackstageLink>
      }
    >
      {latest && (
        <>
          <Typography variant="subtitle1">{latest.title}</Typography>
          <Typography variant="body2" color="textSecondary">
            <time dateTime={latest.created_at} title={formatDate(latest.created_at)}>
              Released {formatRelative(latest.created_at)}
            </time>
          </Typography>
        </>
      )}
    </InfoCard>
  );
};

/**
 * One-time "What's new" dialog shown the first time a user opens the home
 * page after a newer release note is published. The last seen release id is
 * kept in the Backstage storage API.
 */
export const WhatsNewDialog = ({ latest }: { latest?: LatestReleaseNote }) => {
  const classes = useStyles();
  const storageApi = useApi(storageApiRef);
  const releaseNotesApi = useApi(releaseNotesApiRef);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!latest) return;
    const snapshot = storageApi.forBucket(STORAGE_BUCKET).snapshot<string>(LAST_SEEN_RELEASE_KEY);
    if (snapshot.presence === 'unknown' || snapshot.value === latest.id) return;

    const published = Date.parse(latest.created_at);
    if (Number.isNaN(published)) return;
    const isFresh = Date.now() - published <= DIALOG_MAX_AGE_DAYS * 24 * 3600 * 1000;
    // Respect the release-notes plugin's own "seen" marker (banner dismissed).
    const alreadySeen = releaseNotesApi.lastSeenDate().toMillis() >= published;
    if (isFresh && !alreadySeen) {
      setOpen(true);
    }
  }, [latest, storageApi, releaseNotesApi]);

  if (!latest) return null;

  const close = () => {
    setOpen(false);
    storageApi.forBucket(STORAGE_BUCKET).set(LAST_SEEN_RELEASE_KEY, latest.id);
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby="devhub-whats-new-title">
      <DialogTitle id="devhub-whats-new-title">What's new in DevHub</DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6">{latest.title}</Typography>
        <Typography variant="caption" color="textSecondary" component="p" className={classes.dialogMeta}>
          {formatDate(latest.created_at)}
          {latest.publisher ? ` - ${latest.publisher}` : ''}
        </Typography>
        <MarkdownContent content={latest.body || latest.excerpt} dialect="gfm" />
      </DialogContent>
      <DialogActions>
        <Button component={BackstageLink} to={releaseNoteLink(latest)} onClick={close}>
          Read full release notes
        </Button>
        <Button color="primary" variant="contained" onClick={close}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};
